import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Idle lock for shared kitchen tablets.
 *
 * Off by default: nothing here can tell a shared tablet from a manager's own desktop, and the app has no
 * "flavour" switch that does. Turn it on per device (localStorage `idle_lock_minutes`), or for a whole deployment
 * at build time (`REACT_APP_IDLE_LOCK_MINUTES`). Un-defer a default-on for the kitchen flavour when one exists.
 */
export const DEFAULT_IDLE_LOCK_MINUTES = 0; // 0 = off. The single default; change it here.
export const IDLE_LOCK_KEY = 'idle_lock_minutes';
const LOCKED_KEY = 'device_locked';
const CHECK_EVERY_MS = 5000;
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel', 'scroll'];

const toMinutes = (raw) => {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export function idleLockMinutes() {
  try {
    const stored = localStorage.getItem(IDLE_LOCK_KEY);
    if (stored !== null && stored !== '') return toMinutes(stored);
  } catch { /* storage blocked: fall through to the deployment default */ }
  const built = process.env.REACT_APP_IDLE_LOCK_MINUTES;
  return built ? toMinutes(built) : DEFAULT_IDLE_LOCK_MINUTES;
}

export function setIdleLockMinutes(minutes) {
  try {
    localStorage.setItem(IDLE_LOCK_KEY, String(toMinutes(minutes)));
  } catch { /* ignore */ }
}

const readLocked = () => {
  try { return localStorage.getItem(LOCKED_KEY) === '1'; } catch { return false; }
};
const writeLocked = (on) => {
  try {
    if (on) localStorage.setItem(LOCKED_KEY, '1'); else localStorage.removeItem(LOCKED_KEY);
  } catch { /* ignore */ }
};

/**
 * @returns {{locked: boolean, unlock: () => void, lock: () => void}}
 * `active` is false when nobody is signed in. The locked flag is kept in localStorage so a reload does not
 * bypass the lock; it is cleared when the person signs out (active goes false).
 */
export default function useIdleLock({ active, minutes = idleLockMinutes() }) {
  const [locked, setLocked] = useState(() => (active ? readLocked() : false));
  const last = useRef(Date.now());
  const enabled = !!active && minutes > 0;

  const lock = useCallback(() => { writeLocked(true); setLocked(true); }, []);
  const unlock = useCallback(() => { writeLocked(false); last.current = Date.now(); setLocked(false); }, []);

  useEffect(() => {
    if (!active) { writeLocked(false); setLocked(false); }
  }, [active]);

  useEffect(() => {
    if (!enabled || locked) return undefined;
    last.current = Date.now();
    const touch = () => { last.current = Date.now(); };
    const check = () => {
      // Compare to wall-clock time, not a long timer: a sleeping tablet pauses timers but not the clock.
      if (Date.now() - last.current >= minutes * 60000) lock();
    };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, touch, { passive: true, capture: true }));
    document.addEventListener('visibilitychange', check);
    const timer = setInterval(check, CHECK_EVERY_MS);
    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, touch, { capture: true }));
      document.removeEventListener('visibilitychange', check);
      clearInterval(timer);
    };
  }, [enabled, locked, minutes, lock]);

  return { locked: !!active && locked, unlock, lock };
}
